import {useRouter} from 'next/router'
import allAlerts from '@/config/alerts.json'
import React, {Dispatch, SetStateAction, useEffect} from 'react'
import axios, {AxiosError} from '@/utils/axios'
import useSWR from 'swr'
import {toast} from 'react-hot-toast'
import {DispatchType, useCartDispatch} from '@/contexts/CartContext'
import { useLoginModal } from '@/contexts/LoginModalContext'
import {fetchCart} from 'CartActions'
import { track } from '@/lib/tracker'
//import {linkUser} from 'CartActions'
type SetFieldErrorType = (field: string, message: string | undefined) => void

// Define fetchUser first
const fetchUser = async () => {
  try {
    const apiUrl =
      process.env.NEXT_PUBLIC_API_URL || 'https://testing.basmti.com/api'

    // Try token-based auth first if available
    const token = localStorage.getItem('auth_token')
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const res = await axios.get(`${apiUrl}/user`, {
      withCredentials: true,
      headers,
    })

    return res.data
  } catch (err) {
    const error = err as AxiosError
    if (error.response?.status === 401) {
      // Clear token if unauthorized
      localStorage.removeItem('auth_token')
      return null
    }
    throw err
  }
}

export default function useAuth(middleware: 'guest' | 'auth' | 'none') {
  const router = useRouter()
  const [isLoading, setIsLoading] = React.useState(false)
  const loadingTimerId = React.useRef<NodeJS.Timeout | null>(null)
  const { locale } = useRouter()
  const alerts = allAlerts[locale as keyof typeof allAlerts] || allAlerts.ar;

  const cartDispatch = useCartDispatch()
  // Login modal controls for 'auth' middleware
  const { 
    setIsOpen, 
    setPostLoginCallback, 
    postLoginCallback, 
    executePostLoginCallback,
    setCurrentView,
    setProductType 
  } = useLoginModal()
  const {
    data: user,
    error,
    mutate,
  } = useSWR('/api/user', fetchUser, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshInterval: 0,
    shouldRetryOnError: false,
    dedupingInterval: 5000,
    focusThrottleInterval: 5000,
  })

  // Check authentication status
  const checkAuth = async () => {
    try {
      // console.log('Checking authentication status...')
      const response = await axios.get('/user', {
        withCredentials: true,
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      // console.log('Auth check response:', response.status)
      return response.status === 200
    } catch (error: unknown) {
      const axiosError = error as AxiosError
      console.error('Auth check error:', {
        error: axiosError,
        message: axiosError.message,
        response: axiosError.response?.status,
        data: axiosError.response?.data,
      })
      // Don't throw, just return false for failed auth
      return false
    }
  }

  React.useEffect(() => {
    if (user && postLoginCallback) {
      executePostLoginCallback();
    }
  }, [user, postLoginCallback, executePostLoginCallback]);

  React.useEffect(() => {
    if (user || error) {
      setIsLoading(false)
    }

    // No need for verifyAuth as SWR will handle the fetch
    // Only take actions if middleware requires auth and user is null
    if (middleware === 'auth' && user === null && !isLoading) {
      // Open login modal and schedule return to this page after login
      // setPostLoginCallback(() => () => router.replace(router.asPath))
      setIsOpen(true)
    }
  }, [user, error, middleware, isLoading])

  const login = async (
    identifier: string,
    password: string,
    setFieldError: SetFieldErrorType,
    setIsOpen: Dispatch<SetStateAction<boolean>>,
    setVerificationError: Dispatch<SetStateAction<boolean>>,
    setVerificationData: Dispatch<SetStateAction<any>>,
    setProductType: any,
    //cartDispatch: DispatchType,
    tCommon?: any, // Add translation function for common strings
    tAlerts?: any 
  ) => {
    setIsLoading(true)
    // Safety timeout
    if (loadingTimerId.current) {
      clearTimeout(loadingTimerId.current)
    }

    loadingTimerId.current = setTimeout(() => {
      // console.warn('Login request timed out - forcing loading state reset')
      setIsLoading(false)
      toast.error('Request timed out. Please try again.')
    }, 10000) // Reduced from 15s to 10s

    try {
      // console.log('Starting login process...')

      // Set the correct base URL for the API
      const apiUrl =
        process.env.NEXT_PUBLIC_API_URL || 'https://testing.basmti.com/api'
      const siteUrl =
        process.env.NEXT_PUBLIC_SITE_URL || 'https://testing.basmti.com'

      // console.log('Using API URL:', apiUrl)
      // console.log('Using Site URL:', siteUrl)

      // Configure axios defaults for all requests in this session
      axios.defaults.withCredentials = true
      axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

      // First get CSRF token
      let csrfToken = ''
      try {
        // console.log('Requesting CSRF cookie...')
        const csrfResponse = await axios.get(`${siteUrl}/sanctum/csrf-cookie`, {
          withCredentials: true,
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          timeout: 5000,
        })

        // Extract token from cookies
        if (typeof document !== 'undefined') {  
          const match = document.cookie.match(
            new RegExp('(^| )XSRF-TOKEN=([^;]+)'),
          )
          if (match) {
            csrfToken = decodeURIComponent(match[2])
            // console.log('CSRF token retrieved successfully')
          }
        }
      } catch (csrfError: any) {
        console.warn(
          'CSRF request failed, continuing anyway:',
          csrfError.message,
        )
      }

      // Then attempt login
      // console.log('Attempting login...')
      const response = await axios.post(
        `${apiUrl}/login`,
        {identifier, password},
        {
          withCredentials: true,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-XSRF-TOKEN': csrfToken,
          },
          timeout: 8000,
        },
      )

      if (response.status === 200) {
        // console.log('Login successful')

        // Save token if provided
        if (response.data && response.data.token) {
          localStorage.setItem('auth_token', response.data.token)
        }

        await track({ event_name: 'login', metadata: {user_id: response?.data?.id} });

        // Get user data immediately after login
        try {
          // Mutate the user data first - tell SWR we're authenticated now
          // This will update the 'user' state without triggering a revalidation yet
          mutate(response.data.user || {authenticated: true}, false)
          
          cartDispatch(fetchCart())
          // Then trigger a revalidation to get the latest user data
          // This happens in the background after the login modal is closed
          mutate()

          await new Promise(resolve => setTimeout(resolve, 2000));
          toast.success(alerts.logged_in)
          //link user with cart
          //cartDispatch(linkUser())
          executePostLoginCallback();
          // Reset loading state
          setIsLoading(false)
          setIsOpen(false);
        } catch (verifyError: any) {
          // Even if verification fails, we consider login successful
          // since the initial login request succeeded
          console.error('User verification error:', verifyError)
          setIsOpen(false)
          toast.success(alerts.logged_in)
          setIsLoading(false)
        }
      } else {
        // Unexpected status code
        console.warn('Unexpected login response status:', response.status)
        setIsLoading(false)
      }
    } catch (error) {
      // Handle errors
      const axiosError = (error as AxiosError) || new Error(tCommon?.('unknownError'))

      console.error('Login error details:', {
        status: axiosError.response?.status,
        data: axiosError.response?.data,
      })

      if (!axiosError.response) {
      toast.error(tAlerts?.('connectionError'))
      } else if (axiosError.response.status === 401) {
        setFieldError('identifier', tCommon?.('invalidCredentials') || 'Invalid credentials')
        setFieldError('password', tCommon?.('invalidCredentials') || 'Invalid credentials')
        toast.error(tAlerts?.('invalidLogin') || 'Invalid email/phone or password')
      } else if (axiosError.response.status === 404) {
        setFieldError('identifier', tCommon?.('invalidCredentials') || 'Invalid credentials')
        setFieldError('password', tCommon?.('invalidCredentials') || 'Invalid credentials')
        toast.error(tAlerts?.('invalidLogin') || 'Invalid email/phone or password')
      } else if (axiosError.response.status === 412) {
        setVerificationError(true)
        const responseData = axiosError.response.data as any
        const user = responseData.user
        setVerificationData({
          email: user?.email,
          phone: {country_code: user?.country_code, number: user?.phone},
        })
      }else if (axiosError.response.status === 422) {
        const responseData = axiosError.response.data as any
        const errors = responseData.errors

        // Handle validation errors
        if (errors) {
          if (errors.identifier) {
            // You can translate specific error messages or use generic ones
            const errorMsg = errors.identifier[0];
            setFieldError('identifier', translateErrorMessage(errorMsg, tCommon))
          }
          if (errors.password) {
            const errorMsg = errors.password[0];
            setFieldError('password', translateErrorMessage(errorMsg, tCommon))
          }
        }

        toast.error(responseData.message || alerts.login_failure)
      } else {
        toast.error(tCommon?.('unknownError'))
      }

      setIsLoading(false)
    } finally {
      // Clear the safety timeout
      if (loadingTimerId.current) {
        clearTimeout(loadingTimerId.current)
        loadingTimerId.current = null
      }

      // Ensure loading state is reset
      setIsLoading(false)
    }
  }

  const csrf = async () => {
    try {
      if (!process.env.NEXT_PUBLIC_SITE_URL) {
        // console.error('NEXT_PUBLIC_SITE_URL is not defined')
        // Return instead of throwing to allow login to continue
        return null
      }

      const csrfUrl = `${process.env.NEXT_PUBLIC_SITE_URL}/sanctum/csrf-cookie`
      // console.log('CSRF request to:', csrfUrl)

      const response = await axios.get(csrfUrl, {
        withCredentials: true,
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        // Add timeout to prevent hanging requests
        timeout: 5000,
      })

      await new Promise(resolve => setTimeout(resolve, 100))

      // Verify CSRF token is set
      const match = document.cookie.match(new RegExp('(^| )XSRF-TOKEN=([^;]+)'))
      if (!match) {
        console.warn(
          'CSRF token not set in cookies, but continuing login process',
        )
      }

      return response
    } catch (error) {
      const axiosError = error as AxiosError
      console.error('CSRF error details:', {
        error: axiosError,
        message: axiosError.message,
        response: axiosError.response,
        request: axiosError.request,
      })

      // Log the error but don't block the login process
      // console.warn('Continuing login despite CSRF error')
      return null
    }
  }

  const logout = async () => {
    try {
      await axios.post(process.env.NEXT_PUBLIC_API_URL + '/logout')
      mutate(null)
      setCurrentView('login')
      setProductType(null)
      setIsOpen(false)
      router.replace('/')
      // ✅ Empty cart locally
      cartDispatch({type: 'EMPTY_CART'})
      await track({ event_name: 'logout' })
    } catch (error) {
      console.error('Logout error:', error)
      // Even if logout fails, clear local state
      mutate(null)
      router.replace('/')
    }
  }

  return {
    user,
    isLoading,
    logout,
    login,
    mutate,
    csrf,
    checkAuth,
  }
}

const translateErrorMessage = (errorMessage: string, tCommon: any) => {
  if (!tCommon) return errorMessage;
  
  // Map common validation error messages to translation keys
  const errorMapping: Record<string, string> = {
    'المعرف مطلوب': 'identifierRequired',
    'كلمة المرور مطلوبة': 'passwordRequired',
    'بيانات الاعتماد هذه لا تتطابق مع سجلاتنا': 'invalidCredentials',
    'هذا الحقل مطلوب': 'fieldRequired',
    'البريد الإلكتروني غير صالح': 'invalidEmail',
    'رقم الهاتف غير صالح': 'invalidPhone',
    'يجب أن يكون البريد الإلكتروني عنوان بريد إلكتروني صالح': 'validEmailRequired',
  };
  
  const translationKey = errorMapping[errorMessage];
  return translationKey ? tCommon(translationKey) : errorMessage;
}